import { Autocomplete, Box, Chip, Table, TableBody, TableCell, TableRow, TextField, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { formatDateTime } from '@src/shell/utils/Formatter'
import { QueueSnapshot } from '@src/tools/dead-letter/types/QueueTypes'
import { useMemo, useState } from 'react'

interface QueueSummaryProps {
  snapshots: QueueSnapshot[]
}

const QueueSummary = ({ snapshots }: QueueSummaryProps) => {
  const { t } = useTranslation()
  const [selectedAccount, setSelectedAccount] = useState<string | null>(null)
  const [queueFilter, setQueueFilter] = useState('')

  const storageAccounts = useMemo(
    () => [...new Set(snapshots.map((s) => s.storageAccountName))].sort(),
    [snapshots],
  )

  const filtered = useMemo(() => {
    let result = snapshots
    if (selectedAccount) result = result.filter((s) => s.storageAccountName === selectedAccount)
    if (queueFilter) result = result.filter((s) => s.queueName.toLowerCase().includes(queueFilter.toLowerCase()))
    return result
  }, [snapshots, selectedAccount, queueFilter])

  const grouped = useMemo(() => {
    const map = new Map<string, { friendlyName: string; storageAccountName: string; queues: QueueSnapshot[] }>()
    for (const s of filtered) {
      const key = s.storageAccountName
      if (!map.has(key)) {
        map.set(key, { friendlyName: s.friendlyName, storageAccountName: s.storageAccountName, queues: [] })
      }
      map.get(key)!.queues.push(s)
    }
    return Array.from(map.values())
  }, [filtered])

  const lastChecked = snapshots.length > 0
    ? formatDateTime(new Date(Math.max(...snapshots.map(s => new Date(s.lastCheckedOn).getTime()))))
    : null

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2 }}>
      <Box sx={{ maxWidth: 500, width: '100%' }}>
        <Typography variant="h6" sx={{ textAlign: 'center', fontWeight: 'bold', mb: 0.5 }}>
          {t('queues.summary')}
        </Typography>
        {lastChecked && (
          <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: 'text.secondary', mb: 2 }}>
            {t('queues.lastChecked', { date: lastChecked })}
          </Typography>
        )}

        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Autocomplete
            size="small"
            options={storageAccounts}
            value={selectedAccount}
            onChange={(_, value) => setSelectedAccount(value)}
            renderInput={(params) => <TextField {...params} label={t('queues.storageAccount')} />}
            sx={{ flex: 1 }}
          />
          <TextField
            size="small"
            label={t('queues.queueNameFilter')}
            value={queueFilter}
            onChange={(e) => setQueueFilter(e.target.value)}
            sx={{ flex: 1 }}
          />
        </Box>

        {grouped.length === 0 && (
          <Typography sx={{ textAlign: 'center', color: 'text.secondary', mt: 4 }}>
            {t('queues.noQueues')}
          </Typography>
        )}

        {grouped.map((group) => (
          <Box key={group.storageAccountName} sx={{ mb: 2 }}>
            <Box sx={{ bgcolor: 'action.hover', px: 1.5, py: 0.5, borderRadius: 1, mb: 0.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {group.friendlyName}{' '}
                <Typography component="span" variant="caption" sx={{ color: 'text.secondary' }}>
                  ({group.storageAccountName})
                </Typography>
              </Typography>
            </Box>
            <Table size="small">
              <TableBody>
                {group.queues.map((q) => {
                  const isPoisonWithMessages = q.isPoison && q.messageCount > 0
                  return (
                    <TableRow key={q.id}>
                      <TableCell
                        sx={{
                          py: 0.3,
                          px: 1.5,
                          fontSize: '0.8rem',
                          border: 'none',
                          color: isPoisonWithMessages ? 'error.main' : 'text.primary',
                          fontWeight: isPoisonWithMessages ? 'bold' : 'normal',
                        }}
                      >
                        {q.queueName}
                      </TableCell>
                      <TableCell sx={{ py: 0.3, px: 1.5, border: 'none', textAlign: 'right', width: 60 }}>
                        {isPoisonWithMessages ? (
                          <Chip size="small" label={q.messageCount} color="error" sx={{ borderRadius: '10px', fontSize: '0.75rem', height: 20, minWidth: 28 }} />
                        ) : (
                          <Typography variant="caption">{q.messageCount}</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </Box>
        ))}
      </Box>
    </Box>
  )
}

export default QueueSummary
